import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Menu, X, PenLine, BookOpen, Users, Trophy, MessageCircle, 
  Star, UserCircle, Settings, LogOut, Bell, Search, Layers, Compass,
  Bookmark, Award, Share2
} from 'lucide-react';
import Logo from './Logo';
import ShareMenu from './ShareMenu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

import NotificationBell from './NotificationBell';

const Navbar = () => {
  const { user, logout, isAdmin, isModerator, api } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadMsgCount, setUnreadMsgCount] = useState(0);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get('/messages/unread-count');
      setUnreadMsgCount(res.data.count || 0);
    } catch { /* non-critical */ }
  }, [user, api]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleReplayTour = async () => {
    try {
      await api.post('/auth/reset-tour');
      navigate('/dashboard');
      window.location.reload();
    } catch (e) {
      // Non-critical error suppressed
    }
  };

  const navLinks = [
    { name: 'Sections', href: '/sections', icon: Layers },
    { name: 'Works', href: '/stories', icon: BookOpen },
    { name: 'Challenges', href: '/challenges', icon: Trophy },
    { name: 'Discover', href: '/discover', icon: Search },
    { name: 'Groups', href: '/groups', icon: Users },
    { name: 'Bookshelf', href: '/members?tab=bookshelf', icon: BookOpen },
    { name: 'Board', href: '/community-board', icon: Layers },
    { name: 'Spotlight', href: '/spotlight', icon: Star },
  ];

  const isActive = (href) => {
    const [path] = href.split('?');
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <nav className="sticky top-0 z-40 glass-nav border-b border-brand-primary/10" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group"
            data-testid="logo-link"
          >
            <Logo size="default" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  isActive(link.href)
                    ? 'bg-brand-primary text-white'
                    : 'text-text-secondary hover:text-brand-primary hover:bg-brand-primary/5'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {user ? (
              <>
                <Link to="/write" data-testid="write-button">
                  <Button className="hidden sm:flex btn-accent gap-2">
                    <PenLine className="w-4 h-4" />
                    Write
                  </Button>
                </Link>
                
                <Link to="/messages" className="relative" data-testid="messages-link">
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <MessageCircle className="w-5 h-5 text-text-secondary" />
                  </Button>
                  {unreadMsgCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-brand-accent text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="unread-messages-badge">
                      {unreadMsgCount > 99 ? '99+' : unreadMsgCount}
                    </span>
                  )}
                </Link>

                <NotificationBell />

                <ShareMenu
                  title="Collectively Write"
                  text="Join Collectively Write — a community where writers own 100% of their work. Share your poetry, fiction, screenplays, and more."
                  url="https://collectivelywrite.com"
                />

                <DropdownMenu>
                  <DropdownMenuTrigger asChild data-testid="user-menu-trigger">
                    <Button variant="ghost" className="relative rounded-full p-0">
                      <Avatar className="h-9 w-9 border-2 border-brand-primary/10">
                        <AvatarImage src={user.avatar_url} alt={user.display_name} />
                        <AvatarFallback className="bg-brand-primary text-white font-medium">
                          {user.display_name?.[0]?.toUpperCase() || user.username?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2">
                      <p className="font-medium text-text-primary">{user.display_name}</p>
                      <p className="text-sm text-text-muted">@{user.username}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2" data-testid="dashboard-link">
                        <UserCircle className="w-4 h-4" />
                        Dashboard
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/my-stories" className="flex items-center gap-2" data-testid="my-stories-link">
                        <BookOpen className="w-4 h-4" />
                        My Works
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="flex items-center gap-2" data-testid="profile-link">
                        <Settings className="w-4 h-4" />
                        Settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={handleReplayTour}
                      className="flex items-center gap-2 cursor-pointer"
                      data-testid="replay-tour-button"
                    >
                      <Compass className="w-4 h-4" />
                      Replay Welcome Tour
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/faq" className="flex items-center gap-2" data-testid="faq-link">
                        <Compass className="w-4 h-4" />
                        How to Navigate
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/reading-list" className="flex items-center gap-2" data-testid="reading-list-link">
                        <Bookmark className="w-4 h-4" />
                        Reading List
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/achievements" className="flex items-center gap-2" data-testid="achievements-link">
                        <Award className="w-4 h-4" />
                        Achievements
                      </Link>
                    </DropdownMenuItem>
                    {(isAdmin || isModerator) && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="flex items-center gap-2 text-brand-accent" data-testid="admin-link">
                            <Settings className="w-4 h-4" />
                            Admin Panel
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={handleLogout} 
                      className="text-red-600"
                      data-testid="logout-button"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" data-testid="login-link">
                  <Button variant="ghost" className="text-text-secondary hover:text-brand-primary">
                    Log in
                  </Button>
                </Link>
                <Link to="/register" data-testid="register-link">
                  <Button className="btn-primary">
                    Join Us
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-brand-primary/10" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive(link.href)
                      ? 'bg-brand-primary text-white'
                      : 'text-text-secondary hover:bg-brand-primary/5'
                  }`}
                >
                  <link.icon className="w-5 h-5" />
                  {link.name}
                </Link>
              ))}
              {user && (
                <Link
                  to="/write"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg bg-brand-accent text-white mt-2"
                >
                  <PenLine className="w-5 h-5" />
                  Write a Story
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
